/**
 * 👤 거래처 담당자 주소록 — 거래처 관리 화면의 둘째 탭 (v2.143.0 · 2026-09-09)
 *
 * 사용자 지시: 「거래처 담당자 메뉴는 명함 등록하는 것처럼 이름·부서·메일주소·연락처 등으로 주소록처럼 관리하자.」
 * 영업진행 담당자 칸 · 딜등록 제안사 명단 · 딜 제안사 칸 원문에서 옮겨 온 사람들이 한 장부로 모여 있고,
 * 여기서 고치고 · 회사에 연결하고 · 새 사람을 넣는다. 영업건을 저장하면 담당자 칸의 새 이름이 여기에도 쌓인다.
 *
 * 회사 칸: 거래처 목록에서 고르면 연결(✓), 목록에 없는 이름을 적으면 「미연결」로 글자만 남는다 — 나중에 거래처가 생기면
 *   같은 창에서 골라 연결한다. 거래처 목록 자체는 admin/mgmt 가 [🏢 거래처] 탭에서 만든다.
 * 같은 회사에 같은 이름이 있으면 서버가 409 로 알려 준다 — 동명이인이면 [그래도 저장].
 *
 * 엑셀 (v2.144.0 · 사용자 지시 「거래처 주소록 엑셀 업로드·다운로드」)
 *   [📥 엑셀] 지금 보이는 줄을 내려받는다(EXCEL_COLUMNS). [📤 엑셀 올리기] 는 두 단계 — 서버가 줄마다 새로/갱신/건너뜀을 판정한
 *   **미리보기 창**을 먼저 보여 주고, [적용] 을 눌러야 주소록이 바뀐다. 갱신은 파일에 값이 있는 칸만 덮어쓴다(빈 칸은 안 지움).
 *
 * ⚠배경을 눌러도 창이 닫히지 않는다 — 닫기는 [취소]·× 로만 (`check:modal`).
 */

import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listContacts, listContactCompanies, createContact, updateContact, deleteContact, emptyContactInput,
  previewContactImport, applyContactImport,
  SOURCE_LABEL, type PartnerContact, type PartnerContactInput, type PartnerCompanyOption, type ImportPreview,
} from '../lib/partner-contact-api';
import { ApiCallError } from '../lib/api';
import { ComboboxInput } from './ComboboxInput';
import { openMailCompose } from '../lib/mail-compose';
import { exportRowsToExcel, dateStamp } from '../lib/excelExport';
import { applyColState, ColumnHeader, useColState, type Column } from './DataTable';
import { ExcelModeGroup } from './ExcelModeButton';
import { ResetMenu } from './ResetButton';
import * as prefsApi from '../lib/preferences-api';
import { useExcelModeTable } from '../lib/useExcelModeTable';
import { useProgressiveRows } from '../lib/useProgressiveRows';
import { useAuth } from '../lib/auth-context';
import type { UserPreferences } from '../lib/types';
import { LoadingLines } from './ui/Skeleton';
import { RowMenu } from './ui/RowMenu';

/**
 * [📥 엑셀] 칸 — 올리기 머리글과 같다(내려받아 고쳐서 그대로 올리는 흐름). 출처·거래처 연결은 올릴 때 무시된다.
 * ⚠라벨을 바꾸면 backend lib/partner-contact/xlsx.ts 의 별칭도 같이 봐야 한다.
 */
const EXCEL_COLUMNS: Column<PartnerContact>[] = [
  { key: 'company', label: '회사', type: 'text', accessor: (c) => c.companyName },
  { key: 'companyEn', label: '회사 (영문)', type: 'text', accessor: (c) => c.companyNameEn },
  { key: 'name', label: '이름', type: 'text', accessor: (c) => c.name },
  { key: 'title', label: '직급', type: 'text', accessor: (c) => c.title },
  { key: 'department', label: '부서', type: 'text', accessor: (c) => c.department },
  { key: 'email', label: '이메일', type: 'text', accessor: (c) => c.email },
  { key: 'mobile', label: '휴대전화', type: 'text', accessor: (c) => c.mobile },
  { key: 'phone', label: '전화', type: 'text', accessor: (c) => c.phone },
  { key: 'note', label: '메모', type: 'text', accessor: (c) => c.note },
  { key: 'active', label: '사용', type: 'text', accessor: (c) => (c.isActive ? '사용' : '안 씀') },
  { key: 'source', label: '출처', type: 'text', accessor: (c) => SOURCE_LABEL[c.source] ?? c.source },
  { key: 'linked', label: '거래처 연결', type: 'text', accessor: (c) => (c.linked ? '연결' : '미연결') },
];

/* ── 화면 표의 칸 정의 — 머리글 정렬 · 🔍 필터 · 📐 내 표 설정 (v2.148.0) ──────────────
 *   딜등록(v2.144.0)·영업진행·매입관리와 같은 공용 부품을 쓴다.
 *   ⭐`defaultOn:true` 인 칸이 새 사용자에게 보이는 기본 표 = 종전 6칸 그대로다. 나머지는 [📐 내 표 설정] 에서 켠다.
 *   ⭐**accessor 가 정렬·🔍 목록의 값**이다(render 결과가 아니다) — 사람이 고를 만한 글자를 돌려줘야 한다.
 *   ⚠긴 글 칸은 `cellClass: 'max-w-0'` + 안쪽 `truncate` + `title` 한 벌. 빼면 표가 화면 폭을 넘는다.
 */
type ContactColumn = Column<PartnerContact> & {
  defaultOn: boolean;
  /** ⚙ 표시 컬럼 메뉴에서만 쓰는 긴 이름 */
  toggleLabel?: string;
  defaultWidthPx?: number;
  cellClass?: string;
};

interface ContactColumnCtx {
  /** 조회 전용(viewer)이면 false — 쓰기 단추를 **아예 그리지 않는다** (v2.197.0) */
  canEdit: boolean;
  onMail: (c: PartnerContact) => void;
  onEdit: (c: PartnerContact) => void;
  onDelete: (c: PartnerContact) => void;
  /** 🔎 중복 의심 (v2.149.0) — 「중복」 칸이 이것으로 값을 만든다 */
  dupIndex: DupIndex;
  dupOn: Record<DupKind, boolean>;
}

const dash = (s: string | null | undefined): string => (s && s.trim() ? s : '-');

/* ── 🔎 중복 의심 (v2.149.0 · 사용자 요청 「이메일 중복 · 이름 중복 · 연락처 중복」) ──────────
 *   서버를 부르지 않는다 — 이 화면은 이미 주소록 전체를 들고 있다(480명 수준).
 *   ⚠셋은 신호의 세기가 다르다. 운영 자료(2026-09-09) 기준:
 *     메일 3그룹 6명 · 연락처 9그룹 26명 · **이름 61그룹 134명**(김혜원이 6개 회사에 있다 = 동명이인).
 *   그래서 조건마다 껐다 켤 수 있게 하고, 묶음이 붙어 보이도록 「중복」 칸으로 정렬한다.
 */
export type DupKind = 'email' | 'phone' | 'name';
export const DUP_LABEL: Record<DupKind, string> = { email: '메일', phone: '연락처', name: '이름' };
/** 정렬해서 묶음을 붙일 때의 세기 순서 — 메일 > 연락처 > 이름 */
const DUP_RANK: DupKind[] = ['email', 'phone', 'name'];

export interface DupHit {
  /** 같은 값을 쓰는 사람 수 (자기 포함) */
  n: number;
  /** 묶음 열쇠 — 같은 값을 쓰는 사람끼리 같다 */
  key: string;
}
export interface DupIndex {
  /** 담당자 id → 조건별로 걸린 내용 */
  byId: Map<number, Partial<Record<DupKind, DupHit>>>;
  /** 조건별 걸린 사람 수 — 체크박스 옆에 보여 준다 */
  counts: Record<DupKind, number>;
}

/** 전화는 숫자만 남겨 비교한다 — 「010-1234-5678」 과 「01012345678」 은 같은 번호다 */
const digits = (s: string | null | undefined): string => (s ?? '').replace(/[^0-9]/g, '');

/**
 * 중복 의심 만들기. **한 사람이 mobile·phone 에 같은 번호를 적어 둔 것은 중복이 아니다**(id 로 센다).
 * 값이 빈 칸이면 그 조건에서는 아예 빠진다 — 「빈 칸끼리 중복」이 되면 온 목록이 걸린다.
 */
export function buildDupIndex(rows: PartnerContact[]): DupIndex {
  const groups: Record<DupKind, Map<string, Set<number>>> = { email: new Map(), phone: new Map(), name: new Map() };
  const add = (kind: DupKind, key: string, id: number): void => {
    if (!key) return;
    const m = groups[kind];
    const s = m.get(key) ?? new Set<number>();
    s.add(id);
    m.set(key, s);
  };
  for (const c of rows) {
    add('email', (c.email ?? '').trim().toLowerCase(), c.id);
    add('name', (c.name ?? '').replace(/\s+/g, '').trim(), c.id);
    for (const t of [c.mobile, c.phone]) add('phone', digits(t), c.id);
  }
  const byId = new Map<number, Partial<Record<DupKind, DupHit>>>();
  const counts: Record<DupKind, number> = { email: 0, phone: 0, name: 0 };
  for (const kind of DUP_RANK) {
    for (const [key, ids] of groups[kind]) {
      if (ids.size < 2) continue;
      counts[kind] += ids.size;
      for (const id of ids) {
        const cur = byId.get(id) ?? {};
        cur[kind] = { n: ids.size, key };
        byId.set(id, cur);
      }
    }
  }
  return { byId, counts };
}

/** 이 사람이 지금 켠 조건 중 하나라도 걸리나 */
export function dupHits(index: DupIndex, id: number, on: Record<DupKind, boolean>): DupKind[] {
  const hit = index.byId.get(id);
  if (!hit) return [];
  return DUP_RANK.filter((k) => on[k] && hit[k]);
}

function buildContactColumns(ctx: ContactColumnCtx): ContactColumn[] {
  return [
    {
      // ⚠`defaultWidthPx` 는 **희망 폭**일 뿐이다(ColumnHeader 가 width 만 주고 minWidth 는 안 준다) —
      //   좁은 화면에서 무너지면 안 되는 칸은 td 에 `min-w-*` 로 **바닥**을 준다 (v2.149.1 · 종전 th 의 min-w 를 되살림)
      key: 'company', label: '회사', type: 'text', defaultOn: true, defaultWidthPx: 170, cellClass: 'min-w-[170px]',
      accessor: (c) => c.companyName,
      render: (c) => (
        <>
          <div className="font-semibold text-slate-800">
            {c.companyName || <span className="text-slate-400">(회사 없음)</span>}
            {!c.linked ? <span className="ml-1 px-1 rounded bg-amber-100 text-amber-800 text-[11px] font-semibold align-middle" title="거래처 목록에 없는 회사 — 글자로만 적혀 있습니다">미연결</span> : null}
          </div>
          {c.companyNameEn && c.companyNameEn !== c.companyName ? <div className="text-slate-500">{c.companyNameEn}</div> : null}
        </>
      ),
    },
    {
      key: 'person', label: '담당자', toggleLabel: '담당자 (이름 + 직급 · 부서 · 출처 · 건수)', type: 'text',
      defaultOn: true, defaultWidthPx: 170,
      // 정렬·필터는 **이름**으로 — 한 칸에 여러 값을 뭉쳐 보여도 고르는 값은 하나여야 한다
      accessor: (c) => c.name,
      render: (c) => (
        <>
          <div className="whitespace-nowrap"><span className="font-semibold text-slate-800">{c.name || '-'}</span>{c.title ? <span className="text-slate-600"> {c.title}</span> : null}</div>
          <div className="text-slate-500 whitespace-nowrap">
            {c.department ? <span>{c.department} · </span> : null}
            <span className="text-slate-400" title="이 줄이 어디서 왔나">{SOURCE_LABEL[c.source] ?? c.source}</span>
            {c.salesCount ? <span className="text-slate-400" title="영업진행 담당자 칸에 이 사람이 적힌 건수"> · 영업 {c.salesCount}</span> : null}
            {c.dealCount ? <span className="text-slate-400" title="이 사람이 담당자인 딜등록 수"> · 딜 {c.dealCount}</span> : null}
          </div>
        </>
      ),
    },
    {
      // 이메일은 `break-all` 이라 바닥이 없으면 한 글자씩 쪼개져 세로로 길어진다 — 바닥을 준다 (v2.149.1)
      key: 'email', label: '이메일', type: 'text', defaultOn: true, defaultWidthPx: 190, cellClass: 'min-w-[190px]',
      accessor: (c) => c.email,
      render: (c) => <span className="break-all">{c.email || '-'}</span>,
    },
    {
      key: 'contact', label: '연락처', toggleLabel: '연락처 (휴대전화 + 전화 · 두 줄)', type: 'text',
      defaultOn: true, defaultWidthPx: 128, nowrapHeader: true,
      accessor: (c) => c.mobile || c.phone,
      render: (c) => (
        <span className="whitespace-nowrap tabular-nums">
          {c.mobile ? <span className="block">{c.mobile}</span> : null}
          {c.phone ? <span className="block text-slate-500">{c.phone}</span> : null}
          {!c.mobile && !c.phone ? '-' : null}
        </span>
      ),
    },
    { key: 'note', label: '메모', type: 'text', defaultOn: true, defaultWidthPx: 160, cellClass: 'max-w-0 min-w-[120px]', accessor: (c) => c.note, render: (c) => <div className="truncate" title={c.note}>{c.note || ''}</div> },

    /* ── 기본은 꺼져 있는 낱개 칸 — [📐 내 표 설정] 에서 켠다 (한 칸에 뭉쳐 있던 값을 따로 정렬·필터하려는 사람용) ── */
    { key: 'name', label: '이름', type: 'text', defaultOn: false, defaultWidthPx: 90, nowrapHeader: true, accessor: (c) => c.name, render: (c) => <span className="whitespace-nowrap">{dash(c.name)}</span> },
    { key: 'title', label: '직급', type: 'text', defaultOn: false, defaultWidthPx: 80, nowrapHeader: true, accessor: (c) => c.title, render: (c) => <span className="whitespace-nowrap">{dash(c.title)}</span> },
    { key: 'department', label: '부서', type: 'text', defaultOn: false, defaultWidthPx: 110, cellClass: 'max-w-0', accessor: (c) => c.department, render: (c) => <div className="truncate" title={c.department}>{dash(c.department)}</div> },
    { key: 'companyEn', label: '회사 (영문)', type: 'text', defaultOn: false, defaultWidthPx: 150, cellClass: 'max-w-0', accessor: (c) => c.companyNameEn, render: (c) => <div className="truncate" title={c.companyNameEn}>{dash(c.companyNameEn)}</div> },
    { key: 'mobile', label: '휴대전화', type: 'text', defaultOn: false, defaultWidthPx: 110, nowrapHeader: true, accessor: (c) => c.mobile, render: (c) => <span className="whitespace-nowrap tabular-nums">{dash(c.mobile)}</span> },
    { key: 'phone', label: '전화', type: 'text', defaultOn: false, defaultWidthPx: 110, nowrapHeader: true, accessor: (c) => c.phone, render: (c) => <span className="whitespace-nowrap tabular-nums">{dash(c.phone)}</span> },
    { key: 'source', label: '출처', toggleLabel: '출처 (이 줄이 어디서 왔나)', type: 'text', defaultOn: false, defaultWidthPx: 96, nowrapHeader: true, accessor: (c) => SOURCE_LABEL[c.source] ?? c.source },
    { key: 'linked', label: '거래처 연결', type: 'text', align: 'center', defaultOn: false, defaultWidthPx: 84, nowrapHeader: true, accessor: (c) => (c.linked ? '연결' : '미연결'), render: (c) => (c.linked ? <span className="text-emerald-700">연결</span> : <span className="text-amber-700">미연결</span>) },
    { key: 'active', label: '사용', type: 'text', align: 'center', defaultOn: false, defaultWidthPx: 64, nowrapHeader: true, accessor: (c) => (c.isActive ? '사용' : '안 씀') },
    { key: 'salesCount', label: '영업 건수', type: 'number', align: 'right', defaultOn: false, defaultWidthPx: 76, nowrapHeader: true, accessor: (c) => c.salesCount ?? 0 },
    { key: 'dealCount', label: '딜 건수', type: 'number', align: 'right', defaultOn: false, defaultWidthPx: 70, nowrapHeader: true, accessor: (c) => c.dealCount ?? 0 },
    {
      // 🔎 중복 의심 (v2.149.0) — [🔎 중복 의심] 을 켜면 이 칸이 자동으로 보인다
      key: 'dup', label: '중복', toggleLabel: '중복 의심 (메일 · 연락처 · 이름)', type: 'text',
      defaultOn: false, defaultWidthPx: 132, nowrapHeader: true, cellClass: 'whitespace-nowrap',
      accessor: (c) => dupHits(ctx.dupIndex, c.id, ctx.dupOn).map((k) => DUP_LABEL[k]).join(' · '),
      /** 같은 묶음이 붙어 보이도록 — 가장 센 조건(메일>연락처>이름)의 열쇠로 정렬한다 */
      sortAccessor: (c) => {
        const hit = ctx.dupIndex.byId.get(c.id);
        if (!hit) return 'zz';
        for (let i = 0; i < DUP_RANK.length; i += 1) {
          const k = DUP_RANK[i]!;
          if (ctx.dupOn[k] && hit[k]) return `${i}|${hit[k]!.key}`;
        }
        return 'zz';
      },
      render: (c) => {
        const hit = ctx.dupIndex.byId.get(c.id);
        const ks = dupHits(ctx.dupIndex, c.id, ctx.dupOn);
        if (!ks.length) return <span className="text-slate-300">-</span>;
        return (
          <span className="inline-flex gap-1">
            {ks.map((k) => (
              <span
                key={k}
                title={`같은 ${DUP_LABEL[k]}을(를) 쓰는 사람 ${hit![k]!.n}명 — 「${hit![k]!.key}」`}
                className={`px-1 rounded text-[11px] font-semibold ${
                  k === 'email' ? 'bg-rose-100 text-rose-700' : k === 'phone' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-600'
                }`}
              >
                {DUP_LABEL[k]} {hit![k]!.n}
              </span>
            ))}
          </span>
        );
      },
    },

    {
      // 단추 칸 — 값이 없으므로 정렬·🔍 를 아예 안 붙인다 (v2.149.0 · noSort/noFilter)
      key: 'actions', label: '', toggleLabel: ctx.canEdit ? '단추 (✉ 메일 · 수정 · 삭제)' : '단추 (✉ 메일)', type: 'text', align: 'center',
      // 조회 전용은 [✉] 하나뿐이라 좁게 (v2.250.0)
      defaultOn: true, defaultWidthPx: ctx.canEdit ? 76 : 52, nowrapHeader: true, cellClass: 'whitespace-nowrap',
      noSort: true, noFilter: true,
      accessor: () => '',
      /* 조회 전용(viewer)에게는 [✉ 메일] **만** — 수정·삭제는 쓰기라 안 그린다(서버도 sales 이상).
         v2.250.0 전에는 메일 자체가 sales 이상이라 ✉ 도 뺐다(「못 쓰는 단추를 보여 주는 것이 제일 나쁘다」) —
         이제 viewer 도 메일을 쓴다(사용자 10-14 「메일 기능 뷰어 계정들도」).
         v2.317.0 · 수정·삭제는 줄 끝 ▼ 메뉴 한 칸으로(Salesforce 식) — 자주 쓰는 ✉ 만 바로 보이는 단추로 남긴다. */
      render: (c) => (
        <span className="inline-flex items-center gap-1">
          <button
            type="button"
            onClick={(ev) => { ev.stopPropagation(); ctx.onMail(c); }}
            disabled={!c.email}
            title={c.email ? `${c.email} 로 메일 쓰기` : '메일 주소가 없습니다'}
            className="btn btn-outline btn-sm"
          >
            ✉
          </button>
          {ctx.canEdit && (
            <RowMenu
              items={[
                { label: '수정', onClick: () => ctx.onEdit(c) },
                { label: '삭제', tone: 'danger', onClick: () => ctx.onDelete(c) },
              ]}
            />
          )}
        </span>
      ),
    },
  ];
}

const CONTACT_DEFAULT_SORT = [{ key: 'company', dir: 'asc' as const }];
/** [🔎 중복 의심] 을 켜면 정렬을 이 칸으로 바꿔 같은 묶음이 붙어 보이게 한다 */
const DUP_SORT_KEY = 'dup';
/** 단추 칸 — ⚙ 에서 끌 수 없다(끄면 [수정]·[삭제] 로 가는 길이 사라진다) */
const ACTION_KEY = 'actions';

const ACTION_LABEL: Record<ImportPreview['items'][number]['action'], { text: string; cls: string }> = {
  insert: { text: '새로', cls: 'bg-emerald-100 text-emerald-800' },
  update: { text: '갱신', cls: 'bg-blue-100 text-blue-800' },
  skip: { text: '건너뜀', cls: 'bg-slate-100 text-slate-600' },
};

type Editing = { mode: 'new' } | { mode: 'edit'; id: number };

/** 회사 이름 글자로 거래처 목록에서 찾기 — 공백·대소문자만 무시(정규화 대조는 서버가 norm_company 로 한 번 더 한다) */
function findCompany(companies: PartnerCompanyOption[] | undefined, name: string): PartnerCompanyOption | null {
  const key = name.replace(/\s+/g, '').toLowerCase();
  if (!key) return null;
  return companies?.find((c) => c.name.replace(/\s+/g, '').toLowerCase() === key) ?? null;
}

function inputOf(c: PartnerContact): PartnerContactInput {
  return {
    partnerCompanyId: c.partnerCompanyId, companyName: c.linked ? c.partnerName : c.companyNameRaw || c.companyName,
    companyNameEn: c.companyNameEn, name: c.name, department: c.department, title: c.title,
    email: c.email, mobile: c.mobile, phone: c.phone, note: c.note, isActive: c.isActive,
  };
}

export function PartnerContactTab(): React.JSX.Element {
  const qc = useQueryClient();
  const { user } = useAuth();
  /**
   * 고칠 수 있나 — 조회 전용(viewer)은 **보기만** 한다 (v2.197.0 · 사용자 지시).
   * ⛔이 값은 화면을 정리할 뿐이다. 진짜 차단은 서버(`/partner-contacts` 의 쓰기 통로 = sales 이상).
   */
  const canEdit = user?.role !== 'viewer';
  const [search, setSearch] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [onlyUnlinked, setOnlyUnlinked] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  /** 🔎 중복 의심만 보기 (v2.149.0) + 어떤 조건을 볼지 — 셋 다 켠 채로 시작한다 */
  const [dupOnly, setDupOnly] = useState(false);
  const [dupOn, setDupOn] = useState<Record<DupKind, boolean>>({ email: true, phone: true, name: true });
  const [editing, setEditing] = useState<Editing | null>(null);
  const [form, setForm] = useState<PartnerContactInput>(emptyContactInput);
  const [error, setError] = useState('');
  const [dup, setDup] = useState<{ id: number; label: string } | null>(null);
  // 엑셀 올리기 — 1단계 미리보기(자료 무변경) → 사람이 표를 보고 [적용]
  const fileRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [showSkipped, setShowSkipped] = useState(false);
  const [notice, setNotice] = useState('');

  const contacts = useQuery({
    queryKey: ['partnerContacts', showInactive],
    queryFn: () => listContacts({ inactive: showInactive }),
    staleTime: 30_000,
  });
  const companies = useQuery({ queryKey: ['partnerContactCompanies'], queryFn: listContactCompanies, staleTime: 5 * 60_000 });

  const all = contacts.data?.rows ?? [];
  const counts = contacts.data?.counts;

  const companyOptions = useMemo(() => {
    const s = new Set<string>();
    for (const c of all) if (c.companyName) s.add(c.companyName);
    return [...s].sort((a, b) => a.localeCompare(b, 'ko'));
  }, [all]);

  /** 🔎 중복 의심 — **주소록 전체**로 센다(검색·회사 필터 뒤로 세면 「혼자 남아서 중복이 아님」이 된다) */
  const dupIndex = useMemo(() => buildDupIndex(all), [all]);

  /** 위쪽 칸(검색·회사·미연결만·중복 의심)까지만 적용한 줄 — 🔍 머리글 필터의 「고를 수 있는 값」 목록은 이 배열로 만든다 */
  const baseRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all
      .filter((c) => !companyFilter || c.companyName === companyFilter)
      .filter((c) => !onlyUnlinked || !c.linked)
      .filter((c) => !dupOnly || dupHits(dupIndex, c.id, dupOn).length > 0)
      .filter((c) => !q || `${c.companyName} ${c.companyNameEn} ${c.name} ${c.department} ${c.title} ${c.email} ${c.mobile} ${c.phone} ${c.note}`.toLowerCase().includes(q));
  }, [all, search, companyFilter, onlyUnlinked, dupOnly, dupOn, dupIndex]);

  /* ── 머리글 정렬 · 🔍 필터 (v2.148.0) — 정렬·필터는 sessionStorage, 켠 칸·순서·너비는 서버 환경설정에 따로 산다 ── */
  const colState = useColState({ storageKey: 'partner-contacts', initialSort: CONTACT_DEFAULT_SORT });

  const refresh = (): void => {
    void qc.invalidateQueries({ queryKey: ['partnerContacts'] });
    void qc.invalidateQueries({ queryKey: ['partnerContactSuggest'] });
  };
  const closeForm = (): void => { setEditing(null); setError(''); setDup(null); };
  const onSaveError = (e: Error): void => {
    if (e instanceof ApiCallError && e.code === 'CONFLICT') {
      const d = (e.details ?? {}) as { duplicateId?: number; duplicateLabel?: string };
      setDup({ id: d.duplicateId ?? 0, label: d.duplicateLabel ?? '' });
      setError(e.message);
      return;
    }
    setError(e.message);
  };

  const create = useMutation({
    mutationFn: (input: PartnerContactInput) => createContact(input),
    onSuccess: () => { closeForm(); refresh(); },
    onError: onSaveError,
  });
  const update = useMutation({
    mutationFn: ({ id, input }: { id: number; input: PartnerContactInput }) => updateContact(id, input),
    onSuccess: () => { closeForm(); refresh(); },
    onError: onSaveError,
  });
  const remove = useMutation({
    mutationFn: (id: number) => deleteContact(id),
    onSuccess: refresh,
    onError: (e: Error) => window.alert(`지우지 못했습니다 — ${e.message}`),
  });
  const saving = create.isPending || update.isPending;

  const startNew = (): void => {
    setError(''); setDup(null);
    const f = emptyContactInput();
    // 회사 필터를 걸어 놓고 [＋ 담당자 추가] 를 누르면 그 회사로 시작한다
    if (companyFilter) {
      const found = findCompany(companies.data, companyFilter);
      f.companyName = companyFilter;
      f.partnerCompanyId = found?.id ?? null;
    }
    setForm(f);
    setEditing({ mode: 'new' });
  };
  const startEdit = (c: PartnerContact): void => {
    setError(''); setDup(null);
    setForm(inputOf(c));
    setEditing({ mode: 'edit', id: c.id });
  };
  const onCompanyName = (v: string): void => {
    const found = findCompany(companies.data, v);
    setForm({ ...form, companyName: v, partnerCompanyId: found?.id ?? null });
  };
  const submit = (force: boolean): void => {
    setError('');
    if (!form.companyName.trim() && !form.companyNameEn.trim()) { setError('회사를 고르거나 적어 주세요.'); return; }
    if (!form.name.trim() && !form.email.trim() && !form.mobile.trim() && !form.phone.trim()) { setError('이름 · 이메일 · 전화 중 하나는 적어 주세요.'); return; }
    const input = { ...form, force };
    if (!editing) return;
    if (editing.mode === 'new') create.mutate(input);
    else update.mutate({ id: editing.id, input });
  };
  const askDelete = (c: PartnerContact): void => {
    const extra = [
      c.requestCount ? `정보 요청 기록 ${c.requestCount}건은 남습니다(담당자 칸만 비워집니다).` : '',
      c.dealCount ? `딜등록 ${c.dealCount}건의 담당자 칸이 비워집니다.` : '',
    ].filter(Boolean).join('\n');
    if (!window.confirm(`「${c.companyName} ${c.label}」 을(를) 주소록에서 지울까요?${extra ? '\n' + extra : ''}`)) return;
    remove.mutate(c.id);
  };
  const mailTo = (c: PartnerContact): void => {
    openMailCompose({ to: [c.email] });
  };

  /* ── 📐 내 표 설정 (v2.148.0) — 켠 칸·순서·너비는 서버 환경설정(app_user.preferences)에 사람마다 저장된다 ── */
  const prefsQ = useQuery({ queryKey: ['preferences'], queryFn: prefsApi.getPreferences, staleTime: 5 * 60_000 });
  const patchPrefs = useMutation({
    mutationFn: (next: Partial<UserPreferences>) => prefsApi.patchPreferences(next),
    onSuccess: (newPrefs) => qc.setQueryData(['preferences'], newPrefs),
  });
  /**
   * 화면에 그리는 칸 — 단추 핸들러와 **중복 의심 판정**이 들어가야 해서 여기서 만든다.
   * ⭐이 배열을 정렬·필터 계산(applyColState)과 🔍 목록에도 그대로 쓴다 —
   *   「중복」 칸은 dupIndex 를 알아야 값이 나오므로, 모듈 최상단의 정적 배열로는 정렬이 안 된다.
   */
  const columns = useMemo<ContactColumn[]>(
    () => buildContactColumns({ canEdit, onMail: mailTo, onEdit: startEdit, onDelete: askDelete, dupIndex, dupOn }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dupIndex, dupOn, canEdit],
  );
  /** 실제로 표에 그리는 줄 — 숨긴 칸의 필터도 걸리도록 **전체 칸 정의**로 계산한다 */
  const rows = useMemo(
    () => applyColState(baseRows, columns, colState),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [baseRows, columns, colState.sortStack, colState.filters],
  );

  /* ⚡ 처음 목록을 빨리 보이게 (v2.222.2 · 사용자 「처음 목록 나올 때 느려」) — 첫 80줄 먼저 · 나머지는 한가할 때 이어 붙이기.
   *   591줄을 한 번에 그리면 표 배치·그리기에 화면이 0.5초 넘게 멈췄다. 규칙은 lib/useProgressiveRows.ts 한 곳(거래처 탭과 같이 쓴다).
   *   「어떤 목록을 보고 있나」 열쇠(검색·필터·정렬)가 바뀌면 첫 줄부터. 건수·엑셀은 전체(`rows`)로 센다. */
  const listKey = useMemo(() => JSON.stringify([
    search.trim(), companyFilter, onlyUnlinked, dupOnly, dupOn, showInactive, colState.sortStack,
    Object.entries(colState.filters).filter(([, s]) => s && s.size > 0).map(([k, s]) => [k, [...s].sort()]),
  ]), [search, companyFilter, onlyUnlinked, dupOnly, dupOn, showInactive, colState.sortStack, colState.filters]);
  const { drawnRows, pendingRows } = useProgressiveRows(rows, listKey);
  const tbl = useExcelModeTable({
    prefs: prefsQ.data,
    fullColumns: columns as unknown as (Column<unknown> & { defaultOn: boolean })[],
    prefsKey: {
      excelMode: 'partnerContactTabExcelMode',
      columns: 'partnerContactTabColumns',
      columnsKnown: 'partnerContactTabColumnsKnown',
      order: 'partnerContactTabColumnOrder',
      widths: 'partnerContactTabColumnWidths',
    },
    patch: (next) => patchPrefs.mutate(next),
  });
  /**
   * 보이는 칸 — [🔎 중복 의심] 을 켜면 「중복」 칸을 **저장된 설정과 무관하게** 끼워 넣는다.
   * (왜 걸렸는지 안 보이면 목록만 줄어들어 이유를 알 수 없다. 끄면 원래대로 돌아가고 저장값은 안 건드린다)
   */
  const visibleColumns = useMemo<ContactColumn[]>(() => {
    let cols = tbl.visibleColumns as unknown as ContactColumn[];
    if (dupOnly && !cols.some((c) => c.key === DUP_SORT_KEY)) {
      const dupCol = columns.find((c) => c.key === DUP_SORT_KEY);
      if (dupCol) cols = [dupCol, ...cols];
    }
    // ⭐단추 칸은 끌 수 없다 — 끄면 [✉]·[수정]·[삭제] 로 가는 길이 화면에서 사라진다 (v2.149.0)
    //   조회 전용(viewer)도 v2.250.0 부터 [✉] 가 있어 이 칸을 둔다(좁게 — 단추 정의 쪽 52px). v2.197.0~v2.249.0 은 칸째 뺐다.
    if (!cols.some((c) => c.key === ACTION_KEY)) {
      const act = columns.find((c) => c.key === ACTION_KEY);
      if (act) cols = [...cols, act];
    }
    return cols;
  }, [tbl.visibleColumns, dupOnly, columns, canEdit]);
  const [colMenuOpen, setColMenuOpen] = useState(false);

  /** [🔎 중복 의심] 켜기/끄기 — 켜면 같은 묶음이 붙어 보이도록 「중복」 칸으로 정렬한다 */
  const toggleDupOnly = (next: boolean): void => {
    setDupOnly(next);
    if (next) colState.setSort(DUP_SORT_KEY, 'asc');
    else colState.setSort(CONTACT_DEFAULT_SORT[0]!.key, CONTACT_DEFAULT_SORT[0]!.dir);
  };
  /** 지금 켠 조건으로 걸리는 사람 수 — 단추에 보여 준다 */
  const dupCount = useMemo(
    () => all.filter((c) => dupHits(dupIndex, c.id, dupOn).length > 0).length,
    [all, dupIndex, dupOn],
  );

  /** [필터·정렬 초기화 ▼] — 「모두 해제」는 위쪽 칸까지 비운다(안 그러면 「초기화했는데 안 돌아온다」가 된다) */
  const clearListState = (): void => {
    colState.resetAll();
    setSearch('');
    setCompanyFilter('');
    setOnlyUnlinked(false);
    setDupOnly(false);
  };
  const resetToDefault = (): void => {
    clearListState();
    setShowInactive(false);
    setDupOn({ email: true, phone: true, name: true });
    colState.setSort(CONTACT_DEFAULT_SORT[0]!.key, CONTACT_DEFAULT_SORT[0]!.dir);
  };

  /* ── 엑셀 내려받기 — 지금 보이는 줄(검색·회사·미연결 필터 적용) 그대로 ── */
  const downloadExcel = (): void => {
    // ⚠파일 이름과 안에 든 줄이 어긋나면 나중에 그 파일을 「전체 명단」으로 알고 쓴다 —
    //   머리글 🔍 필터·중복 의심으로 좁힌 것도 이름에 남긴다 (v2.149.0)
    const scope = [
      companyFilter, onlyUnlinked ? '미연결' : '', dupOnly ? '중복의심' : '',
      search.trim() ? '검색' : '', rows.length !== baseRows.length ? '필터' : '',
    ].filter(Boolean).join('_');
    exportRowsToExcel({
      filename: `거래처담당자_${scope || '전체'}_${dateStamp()}.xlsx`,
      sheetName: '거래처 담당자',
      columns: EXCEL_COLUMNS,
      rows,
      colWidths: { company: 24, companyEn: 20, name: 10, title: 8, department: 12, email: 28, mobile: 15, phone: 15, note: 30, active: 6, source: 10, linked: 8 },
    });
  };

  /* ── 엑셀 올리기 — 파일을 고르면 서버가 판정만 돌려준다(미리보기 창) ── */
  const onPickFile = async (file: File | null): Promise<void> => {
    if (!file) return;
    setPreviewBusy(true);
    setNotice('');
    try {
      const p = await previewContactImport(file);
      setShowSkipped(false);
      setPreview(p);
    } catch (e) {
      window.alert(`엑셀을 읽지 못했습니다 — ${(e as Error).message}`);
    } finally {
      setPreviewBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };
  const applyImport = useMutation({
    mutationFn: (p: ImportPreview) => applyContactImport(p.items.filter((it) => it.action !== 'skip')),
    onSuccess: (r) => {
      setPreview(null);
      refresh();
      setNotice(`📤 엑셀 올리기 완료 — 새로 ${r.inserted}명 · 갱신 ${r.updated}명${r.missing ? ` · 미리보기 뒤 지워져 못 고친 ${r.missing}명` : ''}${r.duplicate ? ` · 이미 들어 있어 건너뛴 ${r.duplicate}명` : ''}`);
    },
    onError: (e: Error) => window.alert(`적용하지 못했습니다 — ${e.message}`),
  });

  const companyLinked = form.partnerCompanyId !== null;
  const linkedName = companies.data?.find((c) => c.id === form.partnerCompanyId)?.name ?? '';
  const companyNames = useMemo(() => (companies.data ?? []).filter((c) => c.isActive).map((c) => c.name), [companies.data]);

  return (
    <div className="bg-white rounded border border-slate-200">
      {/* 도구 줄 */}
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-slate-100">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="회사 · 이름 · 부서 · 메일 · 전화 검색"
          className="ui-input w-64"
        />
        <select
          value={companyFilter}
          onChange={(e) => setCompanyFilter(e.target.value)}
          className="ui-input max-w-[240px]"
          title="회사로 좁혀 보기"
        >
          <option value="">회사 전체</option>
          {companyOptions.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        <label className="flex items-center gap-1.5 text-xs text-slate-600">
          <input type="checkbox" checked={onlyUnlinked} onChange={(e) => setOnlyUnlinked(e.target.checked)} />
          미연결만 <span className="text-slate-400">(거래처 목록에 없는 회사)</span>
        </label>
        <label className="flex items-center gap-1.5 text-xs text-slate-600">
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          안 쓰는 담당자도 보기
        </label>
        {/* 🔎 중복 의심 (v2.149.0) — 메일·연락처·이름이 겹치는 사람만 모아 본다 */}
        <button
          type="button"
          onClick={() => toggleDupOnly(!dupOnly)}
          title="이메일 · 연락처 · 이름이 다른 사람과 겹치는 담당자만 모아 봅니다 (같은 묶음끼리 붙여서)"
          className={`btn btn-sm ${dupOnly ? 'btn-danger-solid' : 'btn-danger'}`}
        >
          🔎 중복 의심 {dupCount}
        </button>
        <div className="ml-auto flex items-center gap-2">
          {/* 정렬·필터 초기화 · 📐 내 표 설정 (v2.148.0) — 딜등록·영업진행과 같은 자리 */}
          <ResetMenu
            defaultLabel="회사 이름순 · 검색·필터 없음 · 안 쓰는 사람 숨김"
            onResetDefault={resetToDefault}
            onResetAll={clearListState}
          />
          <ExcelModeGroup
            enabled={tbl.excelMode}
            hasPrefs={tbl.hasPrefs}
            userName={user?.name ?? '사용자'}
            onChange={tbl.setExcelMode}
            onReset={tbl.handleReset}
          />
          {/* 엑셀 내려받기·올리기 (v2.144.0) — 내려받은 파일을 고쳐 그대로 올리면 갱신, 새 사람은 추가 */}
          <button
            type="button"
            onClick={downloadExcel}
            disabled={rows.length === 0}
            title="지금 보이는 줄을 엑셀로 (같은 모양으로 고쳐 올리면 갱신됩니다)"
            className="btn btn-secondary"
          >
            📥 엑셀
          </button>
          {/* ⚠쓰기 단추는 조회 전용(viewer)에게 안 보인다 (v2.197.0) — 내려받기는 「읽기」라 그대로 둔다.
              ⛔감추는 것으로 끝내지 않는다: 올리기·추가 통로는 서버가 sales 이상으로 막는다. */}
          {canEdit ? (
            <>
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx,.xlsm,.xls"
                className="hidden"
                onChange={(e) => void onPickFile(e.target.files?.[0] ?? null)}
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={previewBusy}
                title="엑셀 파일을 올리면 먼저 새로/갱신/건너뜀 표를 보여 주고, [적용] 을 눌러야 주소록이 바뀝니다"
                className="btn btn-success"
              >
                {previewBusy ? '읽는 중…' : '📤 엑셀 올리기'}
              </button>
              <button
                type="button"
                onClick={startNew}
                className="btn btn-primary"
              >
                ＋ 담당자 추가
              </button>
            </>
          ) : null}
        </div>
      </div>

      {notice ? (
        <div className="mx-3 mt-2 px-3 py-2 rounded border border-emerald-200 bg-emerald-50 text-sm text-emerald-800 flex items-center">
          <span>{notice}</span>
          <button type="button" onClick={() => setNotice('')} className="ml-auto text-emerald-700 hover:text-emerald-900 text-base leading-none" aria-label="닫기">×</button>
        </div>
      ) : null}

      {/* 🔎 중복 의심 — 조건 고르기. 셋은 신호의 세기가 다르다(이름은 동명이인이 많아 목록을 뒤덮는다) */}
      {dupOnly ? (
        <div className="px-3 py-2 border-b border-rose-100 bg-rose-50/50 text-xs flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="font-semibold text-rose-800">🔎 중복 의심만 보는 중</span>
          {(['email', 'phone', 'name'] as DupKind[]).map((k) => (
            <label key={k} className="flex items-center gap-1.5 text-slate-700">
              <input
                type="checkbox"
                checked={dupOn[k]}
                onChange={(e) => setDupOn({ ...dupOn, [k]: e.target.checked })}
              />
              {DUP_LABEL[k]} 중복 <span className="text-slate-400">{dupIndex.counts[k]}명</span>
            </label>
          ))}
          <span className="text-slate-500">
            같은 값을 쓰는 사람끼리 붙여서 보여 줍니다 · 줄을 눌러 고치거나 [삭제]로 정리하세요
          </span>
          <span className="ml-auto text-slate-400">
            ⚠「이름 중복」은 대부분 동명이인입니다 — 회사가 다르면 다른 사람일 수 있습니다
          </span>
        </div>
      ) : null}

      <div className="px-3 py-1.5 text-xs text-slate-500 border-b border-slate-100 flex flex-wrap gap-x-3">
        {counts ? (
          <>
            {/* ⚠이 숫자는 서버가 센 「사용 중인 사람」이다 — 아래 「N건」과 기준이 달라 이름을 정확히 적는다 (v2.149.1) */}
            <span>사용 중 담당자 <b className="text-slate-700">{counts.active}</b>명</span>
            <span>회사 <b className="text-slate-700">{counts.companies}</b>곳</span>
            <span>미연결 <b className={counts.unlinked ? 'text-amber-700' : 'text-slate-700'}>{counts.unlinked}</b>명</span>
            {/* 🔍 머리글 필터만 걸어도 「결과」가 나와야 한다 — 위 숫자와 아래 건수가 따로 놀지 않게 (v2.149.0) */}
            {(search.trim() || companyFilter || onlyUnlinked || dupOnly || rows.length !== all.length)
              ? <span>· 결과 <b className="text-slate-700">{rows.length}</b></span> : null}
            {showInactive ? <span className="text-slate-400">· 안 쓰는 담당자 포함</span> : null}
          </>
        ) : <span><LoadingLines /></span>}
        <span className="ml-auto text-slate-400">{canEdit ? '줄을 누르면 고칠 수 있습니다 · 영업진행에 새 담당자를 적어 저장하면 여기에도 쌓입니다' : '조회 전용 — 보기만 가능합니다'}</span>
      </div>

      {contacts.isError ? (
        <div className="py-8 text-center text-sm text-rose-600">목록을 불러오지 못했습니다 — {(contacts.error as Error).message}</div>
      ) : null}

      {/* ⚙ 표시 컬럼 — [📐 내 표 설정] 을 켰을 때만 (v2.148.0 · 좁은 화면에서는 숨긴다) */}
      {tbl.excelMode ? (
        <div className="hidden md:block px-3 py-2 border-b border-slate-100 text-xs">
          <div className="flex items-center justify-between">
            <button type="button" onClick={() => setColMenuOpen((v) => !v)} className="text-blue-600 hover:underline">
              ⚙ 표시 컬럼 ({visibleColumns.length}/{tbl.fullAllKeys.length}{patchPrefs.isPending ? ' · 저장 중...' : ''}){colMenuOpen ? ' ▲' : ' ▼'}
            </button>
            <span className="text-xs text-slate-500">💡 머리글을 끌면 순서 변경 · 우측 끝을 끌면 너비 조정 · 머리글 클릭은 정렬 (자동 저장)</span>
          </div>
          {colMenuOpen ? (
            <div className="mt-2 grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1">
              {/* 단추 칸은 목록에서 뺀다 — 끌 수 없는 칸이라 체크박스를 보이면 헷갈린다 (v2.149.0) */}
              {columns.filter((d) => d.key !== ACTION_KEY).map((d) => {
                const on = tbl.activeColKeys.has(d.key);
                return (
                  <label key={d.key} className={`flex items-center gap-1.5 px-2 py-1 rounded cursor-pointer text-xs ${on ? 'bg-blue-50 text-blue-800' : 'text-slate-500 hover:bg-slate-50'}`}>
                    <input type="checkbox" checked={on} onChange={() => tbl.toggleColumn(d.key)} disabled={patchPrefs.isPending} />
                    <span>{d.toggleLabel ?? (d.label || '(단추 칸)')}</span>
                  </label>
                );
              })}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="overflow-x-auto">
        {/* dense — 기본 6칸. 머리글은 ColumnHeader 가 그린다(정렬 클릭 · 🔍 필터 · 📐 끌기/너비). 1280 에서 실측 (2026-09-09) */}
        <table className="data-table dense w-full text-xs">
          <thead>
            <tr>
              {visibleColumns.map((col, idx) => (
                <ColumnHeader
                  key={col.key}
                  column={col}
                  baseRows={baseRows}
                  /* ⭐연쇄 필터·숨긴 칸 필터가 살아 있으려면 **전체 칸**을 넘겨야 한다 */
                  columns={columns}
                  colState={colState}
                  excelMode={tbl.excelMode}
                  onReorder={tbl.excelMode ? tbl.handleReorder : undefined}
                  onMoveLeft={tbl.excelMode && idx > 0 ? () => tbl.handleMoveLeft(col.key) : undefined}
                  onMoveRight={tbl.excelMode && idx < visibleColumns.length - 1 ? () => tbl.handleMoveRight(col.key) : undefined}
                  onResize={tbl.excelMode ? tbl.handleResize : undefined}
                  widthPx={tbl.columnWidths?.[col.key] ?? col.defaultWidthPx}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {contacts.isLoading ? (
              <tr><td colSpan={Math.max(visibleColumns.length, 1)} className="py-8 text-center text-slate-400"><LoadingLines /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={Math.max(visibleColumns.length, 1)} className="py-8 text-center text-slate-400">
                {/* 걸러서 0건인지, 정말 비었는지 구별해 준다 — 「자료가 날아갔나」 문의를 막는다 (v2.149.0) */}
                {search.trim() || companyFilter || onlyUnlinked || dupOnly || all.length > 0
                  ? '조건에 맞는 담당자가 없습니다 — [↺ 필터·정렬 초기화] 로 되돌릴 수 있습니다.'
                  : '담당자가 없습니다.'}
              </td></tr>
            ) : drawnRows.map((c) => (
              <tr
                key={c.id}
                /* 조회 전용은 줄을 눌러도 고치기 창이 열리지 않는다 — 손 모양 커서도 안 준다 (v2.197.0) */
                onClick={canEdit ? () => startEdit(c) : undefined}
                className={`${canEdit ? 'cursor-pointer hover:bg-slate-50' : ''} ${c.isActive ? '' : 'text-slate-400'}`}
              >
                {visibleColumns.map((col) => (
                  <td
                    key={col.key}
                    className={`align-top ${col.align === 'center' ? 'text-center' : col.align === 'right' ? 'text-right' : 'text-left'} ${col.cellClass ?? ''} ${col.mobileHidden ? 'hidden md:table-cell' : ''}`}
                  >
                    {col.render ? col.render(c) : (col.accessor(c) ?? '')}
                  </td>
                ))}
              </tr>
            ))}
            {/* ⚡ 아직 안 그린 줄 — 곧 이어 붙는다. 빨리 끝까지 내린 사람이 「목록이 여기서 끝났나」로 오해하지 않게 (v2.222.2) */}
            {!contacts.isLoading && pendingRows > 0 ? (
              <tr>
                <td colSpan={Math.max(visibleColumns.length, 1)} className="py-3 text-center text-slate-400">
                  나머지 {pendingRows}명 불러오는 중…
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <div className="px-3 py-2 text-xs text-slate-500 border-t border-slate-100">
        {rows.length}건{rows.length !== baseRows.length ? ` (필터 전 ${baseRows.length}건)` : ''} ·
        {canEdit ? ' 줄을 누르면 고칠 수 있습니다 · ' : ' 조회 전용입니다(보기만) · '}
        머리글을 누르면 정렬(다시 누르면 반대, 한 번 더 누르면 해제) · 머리글 옆 🔍 로 값 골라 보기 ·
        [📐 내 표 설정] 을 켜면 칸을 고르고 순서·너비를 바꿀 수 있습니다
      </div>

      {/* ── 엑셀 올리기 미리보기 창 — 자료는 [적용] 을 눌러야 바뀐다. 배경 클릭으로 닫지 않는다 (check:modal) ── */}
      {preview ? (
        <div className="fixed inset-0 bg-black/40 z-[80] flex items-start justify-center overflow-y-auto p-4">
          <div className="bg-white rounded shadow-lg w-full max-w-5xl mt-8">
            <div className="px-4 py-3 border-b border-slate-200 text-sm font-semibold flex items-center gap-2">
              📤 엑셀 올리기 — 미리보기
              <span className="text-xs font-normal text-slate-500 truncate" title={preview.filename}>{preview.filename} · {preview.sheetName} 시트 · {preview.rows}줄</span>
              <button type="button" onClick={() => setPreview(null)} disabled={applyImport.isPending} className="ui-close ml-auto" aria-label="닫기">×</button>
            </div>
            <div className="p-4 space-y-3 text-sm">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="px-2 py-1 rounded bg-emerald-100 text-emerald-800 font-semibold">새로 {preview.counts.insert}명</span>
                <span className="px-2 py-1 rounded bg-blue-100 text-blue-800 font-semibold">갱신 {preview.counts.update}명</span>
                <span className="px-2 py-1 rounded bg-slate-100 text-slate-600 font-semibold">건너뜀 {preview.counts.skip}줄</span>
                <label className="flex items-center gap-1.5 text-slate-600 ml-2">
                  <input type="checkbox" checked={showSkipped} onChange={(e) => setShowSkipped(e.target.checked)} />
                  건너뛴 줄도 보기
                </label>
                <span className="ml-auto text-slate-500">
                  읽은 칸: {preview.headerMap.filter((h) => h.field !== 'ignored' && h.field !== 'unknown').map((h) => h.header).join(' · ')}
                </span>
              </div>
              {preview.warnings.length ? (
                <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 space-y-0.5">
                  {preview.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
                </div>
              ) : null}
              <div className="text-xs text-slate-500">
                갱신은 <b>파일에 값이 있는 칸만</b> 덮어씁니다(빈 칸은 있던 값을 지우지 않습니다). 같은 회사·같은 이름이 없으면 새로 들어가고, 회사 이름이 거래처 목록과 맞으면 자동으로 연결됩니다.
              </div>
              <div className="overflow-auto max-h-[52vh] border border-slate-200 rounded">
                <table className="data-table dense w-full text-xs">
                  <thead className="sticky top-0 bg-slate-50">
                    <tr>
                      <th className="text-right w-[48px]">줄</th>
                      <th className="text-center w-[64px]">판정</th>
                      <th className="text-left min-w-[150px]">회사</th>
                      <th className="text-left w-[120px]">이름 · 직급</th>
                      <th className="text-left min-w-[160px]">이메일 · 전화</th>
                      <th className="text-left min-w-[220px]">바뀌는 것 / 사유</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.items.filter((it) => showSkipped || it.action !== 'skip').map((it) => {
                      const a = ACTION_LABEL[it.action];
                      return (
                        <tr key={it.rowNo} className={it.action === 'skip' ? 'text-slate-400' : ''}>
                          <td className="text-right tabular-nums align-top">{it.rowNo}</td>
                          <td className="text-center align-top"><span className={`inline-block px-1.5 py-0.5 rounded font-semibold ${a.cls}`}>{a.text}</span></td>
                          <td className="text-left align-top">
                            {it.companyName || <span className="text-slate-400">(없음)</span>}
                            {it.companyName && !it.companyLinked ? <span className="ml-1 px-1 rounded bg-amber-100 text-amber-800 text-[11px] font-semibold">미연결</span> : null}
                          </td>
                          <td className="text-left align-top whitespace-nowrap">{[it.name, it.title].filter(Boolean).join(' ') || '-'}</td>
                          <td className="text-left align-top break-all">{[it.email, it.mobile, it.phone].filter(Boolean).join(' · ') || '-'}</td>
                          <td className="text-left align-top">
                            {it.action === 'update' ? (
                              <div className="space-y-0.5">
                                {it.changes.map((c) => (
                                  <div key={c.field}><span className="text-slate-500">{c.label}:</span> <span className="line-through text-slate-400">{c.from || '(빈 칸)'}</span> → <b>{c.to}</b></div>
                                ))}
                              </div>
                            ) : (it.reason || (it.action === 'insert' ? '새 사람' : ''))}
                          </td>
                        </tr>
                      );
                    })}
                    {preview.items.filter((it) => showSkipped || it.action !== 'skip').length === 0 ? (
                      <tr><td colSpan={6} className="py-6 text-center text-slate-400">적용할 줄이 없습니다{preview.counts.skip ? ' — 「건너뛴 줄도 보기」 를 켜 보세요' : ''}.</td></tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="px-4 py-3 border-t border-slate-200 flex justify-end gap-2">
              <button type="button" onClick={() => setPreview(null)} disabled={applyImport.isPending} className="btn btn-secondary">취소</button>
              <button
                type="button"
                onClick={() => applyImport.mutate(preview)}
                disabled={applyImport.isPending || preview.counts.insert + preview.counts.update === 0}
                className="btn btn-success-solid"
              >
                {applyImport.isPending ? '적용 중…' : `적용 — 새로 ${preview.counts.insert} · 갱신 ${preview.counts.update}`}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* ── 추가·수정 창 — 배경 클릭으로 닫지 않는다 (check:modal) ── */}
      {editing ? (
        <div className="fixed inset-0 bg-black/40 z-[80] flex items-start justify-center overflow-y-auto p-4">
          <div className="bg-white rounded shadow-lg w-full max-w-2xl mt-10">
            <div className="px-4 py-3 border-b border-slate-200 text-sm font-semibold flex items-center">
              {editing.mode === 'new' ? '👤 담당자 추가' : '👤 담당자 수정'}
              <button type="button" onClick={closeForm} disabled={saving} className="ui-close ml-auto" aria-label="닫기">×</button>
            </div>
            <div className="p-4 space-y-3 text-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <ComboboxInput
                    label="회사 (거래처 목록에서 고르기 · 없으면 그대로 적기)"
                    value={form.companyName}
                    onChange={onCompanyName}
                    suggestions={companyNames}
                    placeholder="주식회사 다인정보기술…"
                  />
                  <div className={`mt-1 text-xs ${companyLinked ? 'text-emerald-700' : form.companyName.trim() ? 'text-amber-700' : 'text-slate-400'}`}>
                    {companyLinked
                      ? `✓ 거래처 「${linkedName}」 에 연결됩니다`
                      : form.companyName.trim()
                        ? '⚠ 거래처 목록에 없는 이름 — 글자로만 저장됩니다(미연결). 거래처가 생기면 여기서 골라 연결하세요'
                        : '거래처 이름을 고르거나 적어 주세요'}
                  </div>
                </div>
                <label className="block">
                  <span className="text-xs text-slate-500">회사 (영문 · 선택)</span>
                  <input value={form.companyNameEn} maxLength={200} onChange={(e) => setForm({ ...form, companyNameEn: e.target.value })} className="ui-input w-full mt-1" />
                </label>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <label className="block">
                  <span className="text-xs text-slate-500">이름 *</span>
                  <input value={form.name} maxLength={100} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="홍길동" className="ui-input w-full mt-1" />
                </label>
                <label className="block">
                  <span className="text-xs text-slate-500">직급</span>
                  <input value={form.title} maxLength={50} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="부장" className="ui-input w-full mt-1" />
                </label>
                <label className="block">
                  <span className="text-xs text-slate-500">부서</span>
                  <input value={form.department} maxLength={100} onChange={(e) => setForm({ ...form, department: e.target.value })} placeholder="영업1팀" className="ui-input w-full mt-1" />
                </label>
                <label className="block">
                  <span className="text-xs text-slate-500">이메일</span>
                  <input value={form.email} maxLength={200} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="name@company.com" className="ui-input w-full mt-1" />
                </label>
                <label className="block">
                  <span className="text-xs text-slate-500">휴대전화</span>
                  <input value={form.mobile} maxLength={50} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="010-0000-0000" className="ui-input w-full mt-1 tabular-nums" />
                </label>
                <label className="block">
                  <span className="text-xs text-slate-500">전화 (사무실)</span>
                  <input value={form.phone} maxLength={50} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="02-000-0000" className="ui-input w-full mt-1 tabular-nums" />
                </label>
              </div>
              <label className="block">
                <span className="text-xs text-slate-500">메모</span>
                <textarea value={form.note} rows={2} maxLength={2000} onChange={(e) => setForm({ ...form, note: e.target.value })} className="ui-input ui-textarea w-full mt-1" />
              </label>
              <label className="flex items-center gap-1.5 text-xs text-slate-700">
                <input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
                사용 (고르기 목록에 나옴 — 퇴사·이직이면 끄기)
              </label>
              {error ? (
                <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1.5">
                  {error}
                  {dup ? <div className="mt-1 text-slate-600">동명이인이 맞으면 아래 [그래도 저장] 을 눌러 주세요.</div> : null}
                </div>
              ) : null}
            </div>
            <div className="px-4 py-3 border-t border-slate-200 flex justify-end gap-2">
              <button type="button" onClick={closeForm} disabled={saving} className="btn btn-secondary">취소</button>
              {dup ? (
                <button type="button" onClick={() => submit(true)} disabled={saving} className="btn btn-warning-solid">
                  그래도 저장 (동명이인)
                </button>
              ) : (
                <button type="button" onClick={() => submit(false)} disabled={saving} className="btn btn-primary">
                  {saving ? '저장 중…' : editing.mode === 'new' ? '주소록에 추가' : '수정 저장'}
                </button>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
